import { Skeleton } from '@/components/_ui/Skeleton'
import T, { useTranslate } from '@/components/Helpers/Translate'
import type { CosmicVariationType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { getSkuAvailability } from '@/lib/hooks/services/useGetSkuAvailability'
import type { HydraGetSkuAvailabilityResponse } from '@/lib/services/Hydra/order/getSkuAvailability'
import { cn } from '@/lib/utils'
import Image from 'next/image'

type Props = {
  selectedVariation: CosmicVariationType | null
  isLoadingProductAvailability: boolean
  productAvailability: HydraGetSkuAvailabilityResponse | null
}

export const ProductAvailability = ({
  selectedVariation,
  isLoadingProductAvailability,
  productAvailability,
}: Props) => {
  const { translate } = useTranslate()

  const renderProductAvailability = () => {
    if (isLoadingProductAvailability) {
      return (
        <Skeleton className="align-center flex flex-row">
          <div className="flex flex-col md:flex-row">
            <span className="pr-1 font-medium">
              <T>loading_product_availability</T>
            </span>
          </div>
        </Skeleton>
      )
    }

    if (productAvailability && productAvailability?.items.length <= 0) {
      return (
        <div className="align-center flex flex-row">
          <Image
            className="mr-5 h-3.5"
            alt={translate('ships_by')}
            src="/icons/warehouse.svg"
            width={15}
            height={15}
          />

          <div className="flex flex-col md:flex-row">
            <span className="space-x-2 pr-1 font-medium">
              <T>availability</T>
              <T>unavailable</T>
            </span>
          </div>
        </div>
      )
    }

    const skuAvailability = getSkuAvailability({
      sku: selectedVariation?.sku,
      productAvailability,
    })

    return (
      <div className="align-center flex flex-row">
        <div className="flex flex-col md:flex-row">
          <span className="space-x-1 pr-1 font-medium">
            <T>availability</T>
            <span
              className={cn(
                'font-bold',
                skuAvailability === 'in_stock' && 'text-green-700',
                skuAvailability === 'out_of_stock' && 'text-red-700',
                skuAvailability === 'low_stock' && 'text-orange-400',
                skuAvailability === 'back_order' && 'text-orange-400',
              )}
            >
              <T>{skuAvailability}</T>
            </span>
          </span>
        </div>
      </div>
    )
  }

  return <div>{renderProductAvailability()}</div>
}
